import { useState, useEffect, useCallback } from 'react';
import PendantPreview from './PendantPreview.jsx';
import ModeConfigForm from './ModeConfigForm.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * ProfileDrawer — Add/Edit drawer for PalPendant display profiles.
 *
 * Replaces the V1's 100+ individual state variables with a single unified
 * modes object. Shows three pendant preview cards at the top (all-at-once)
 * with mode tabs below for editing one mode at a time.
 *
 * Props:
 *   open       — drawer visibility
 *   onClose    — close callback
 *   onSave     — save callback (receives { profileName, modes } for add, or full payload for edit)
 *   saving     — boolean, disables buttons while saving
 *   editTarget — null = add mode, object = edit mode (has dsp_profile_id + modes array)
 *   dataTypes  — pendant data type options array
 */

// ---------------------------------------------------------------------------
// Default mode state factory
// ---------------------------------------------------------------------------
function createDefaultMode() {
    return {
        text_size: 1,
        line_one_data_type_id: '',
        line_one_text: '',
        line_one_text_suffix: false,
        line_two_data_type_id: '',
        line_two_text: '',
        line_two_text_suffix: false,
        line_three_data_type_id: '',
        line_three_text: '',
        line_three_text_suffix: false,
    };
}

function createDefaultModes() {
    return {
        idle: createDefaultMode(),
        panic: createDefaultMode(),
        accepted: createDefaultMode(),
    };
}

/** Map mode_id (1,2,3) to mode key. */
const MODE_ID_MAP = { 1: 'idle', 2: 'panic', 3: 'accepted' };
const MODE_KEY_TO_ID = { idle: 1, panic: 2, accepted: 3 };

const MODE_TABS = [
    { key: 'idle', label: 'Idle', modeId: 1 },
    { key: 'panic', label: 'Panic', modeId: 2 },
    { key: 'accepted', label: 'Accepted', modeId: 3 },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ProfileDrawer = ({ open, onClose, onSave, saving, editTarget, dataTypes }) => {
    // -- Form state --
    const [profileName, setProfileName] = useState('');
    const [modes, setModes] = useState(createDefaultModes);
    const [activeMode, setActiveMode] = useState('idle');
    const [errors, setErrors] = useState({});

    // ======================================================================
    // Initialize form when drawer opens
    // ======================================================================
    useEffect(() => {
        if (!open) return;

        if (editTarget) {
            // Edit mode — populate from existing profile
            setProfileName(editTarget.profile_name || '');
            const newModes = createDefaultModes();

            (editTarget.modes || []).forEach((m) => {
                const key = MODE_ID_MAP[m.mode_id];
                if (!key) return;

                newModes[key] = {
                    dsp_profile_entity_id: m.dsp_profile_entity_id,
                    text_size: m.text_size || 1,
                    line_one_data_type_id: m.line_one_data_type_id || '',
                    line_one_text: m.line_one_text || '',
                    line_one_text_suffix: !!m.line_one_text_suffix,
                    line_two_data_type_id: m.line_two_data_type_id || '',
                    line_two_text: m.line_two_text || '',
                    line_two_text_suffix: !!m.line_two_text_suffix,
                    line_three_data_type_id: m.line_three_data_type_id || '',
                    line_three_text: m.line_three_text || '',
                    line_three_text_suffix: !!m.line_three_text_suffix,
                };
            });

            setModes(newModes);
        } else {
            // Add mode — default state
            setProfileName('');
            setModes(createDefaultModes());
        }

        setActiveMode('idle');
        setErrors({});
    }, [open, editTarget]);

    // ======================================================================
    // Mode field change handler
    // ======================================================================
    const handleModeChange = useCallback((fieldName, value) => {
        // Handle error clearing from ModeConfigForm
        if (fieldName.startsWith('_clearError:')) {
            const errField = fieldName.replace('_clearError:', '');
            setErrors((prev) => {
                const next = { ...prev };
                delete next[`${activeMode}_${errField}`];
                return next;
            });
            return;
        }

        setModes((prev) => {
            const updated = { ...prev };
            const modeData = { ...updated[activeMode] };
            modeData[fieldName] = value;

            // When text_size changes to 2, clear line 3 fields
            if (fieldName === 'text_size' && value === 2) {
                modeData.line_three_data_type_id = '';
                modeData.line_three_text = '';
                modeData.line_three_text_suffix = false;
            }

            updated[activeMode] = modeData;
            return updated;
        });
    }, [activeMode]);

    // ======================================================================
    // Validation
    // ======================================================================
    const validate = () => {
        const errs = {};

        if (!profileName.trim()) {
            errs.profileName = 'Profile name is required.';
        }

        // Validate each mode
        MODE_TABS.forEach(({ key }) => {
            const m = modes[key];

            if (!m.line_one_data_type_id) {
                errs[`${key}_line_one_data_type_id`] = 'Line 1 data type is required.';
            }
            if (!m.line_two_data_type_id) {
                errs[`${key}_line_two_data_type_id`] = 'Line 2 data type is required.';
            }
            // Line 3 required only for 3-line mode
            if (m.text_size === 1 && !m.line_three_data_type_id) {
                errs[`${key}_line_three_data_type_id`] = 'Line 3 data type is required.';
            }
        });

        setErrors(errs);

        // If there are errors in a non-active mode, switch to the first mode with errors
        if (Object.keys(errs).length > 0) {
            const firstErrorKey = Object.keys(errs)[0];
            if (firstErrorKey !== 'profileName') {
                const errorMode = firstErrorKey.split('_')[0];
                if (errorMode !== activeMode && MODE_TABS.some((t) => t.key === errorMode)) {
                    setActiveMode(errorMode);
                }
            }
        }

        return Object.keys(errs).length === 0;
    };

    // ======================================================================
    // Save handler
    // ======================================================================
    const handleSave = () => {
        if (!validate()) return;

        // Build modes payload array
        const modesPayload = MODE_TABS.map(({ key, modeId }) => {
            const m = modes[key];
            const modeData = {
                mode_id: modeId,
                text_size: m.text_size,
                line_one_data_type_id: m.line_one_data_type_id,
                line_one_text: m.line_one_text || '',
                line_one_text_suffix: !!m.line_one_text_suffix,
                line_two_data_type_id: m.line_two_data_type_id,
                line_two_text: m.line_two_text || '',
                line_two_text_suffix: !!m.line_two_text_suffix,
                line_three_data_type_id: m.text_size === 1 ? m.line_three_data_type_id : '',
                line_three_text: m.text_size === 1 ? (m.line_three_text || '') : '',
                line_three_text_suffix: m.text_size === 1 ? !!m.line_three_text_suffix : false,
            };

            // Include edit-specific fields
            if (editTarget) {
                modeData.dsp_profile_entity_id = m.dsp_profile_entity_id;
            }

            return modeData;
        });

        onSave({
            profileName: profileName.trim(),
            modes: modesPayload,
            editTarget,
        });
    };

    // ======================================================================
    // Get errors for the active mode (strip mode prefix for ModeConfigForm)
    // ======================================================================
    const activeModeErrors = {};
    Object.entries(errors).forEach(([key, val]) => {
        if (key.startsWith(`${activeMode}_`)) {
            activeModeErrors[key.replace(`${activeMode}_`, '')] = val;
        }
    });

    // Count errors per mode for tab badges
    const errorCounts = {};
    MODE_TABS.forEach(({ key }) => {
        errorCounts[key] = Object.keys(errors).filter((k) => k.startsWith(`${key}_`)).length;
    });

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title={editTarget ? 'Edit Profile' : 'New Profile'}
                size="lg"
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : editTarget ? 'Save Profile' : 'Create Profile'}
                        </button>
                    </>
                )}
            >
                {/* Profile Name */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">
                        Profile Name <span className="dp-drawer__required">*</span>
                    </label>
                    <input
                        className={`dp-drawer__input${errors.profileName ? ' is-invalid' : ''}`}
                        type="text"
                        value={profileName}
                        onChange={(e) => {
                            setProfileName(e.target.value);
                            if (errors.profileName) {
                                setErrors((prev) => { const next = { ...prev }; delete next.profileName; return next; });
                            }
                        }}
                        placeholder="e.g. Default Profile"
                    />
                    {errors.profileName ? <p className="dp-drawer__error">{errors.profileName}</p> : null}
                </div>

                {/* Three pendant preview cards — side by side */}
                <div className="pp-pendant-row">
                    {MODE_TABS.map(({ key, label }) => (
                        <PendantPreview
                            key={key}
                            modeConfig={modes[key]}
                            dataTypes={dataTypes}
                            label={label}
                            isActive={activeMode === key}
                            onClick={() => setActiveMode(key)}
                        />
                    ))}
                </div>

                {/* Mode tabs */}
                <div className="pp-mode-tabs">
                    {MODE_TABS.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`pp-mode-tab${activeMode === key ? ' is-active' : ''}`}
                            onClick={() => setActiveMode(key)}
                        >
                            {label}
                            {errorCounts[key] > 0 ? (
                                <span className="pp-mode-tab__badge">{errorCounts[key]}</span>
                            ) : null}
                        </button>
                    ))}
                </div>

                {/* Mode config form for the active mode */}
                <ModeConfigForm
                    mode={modes[activeMode]}
                    onChange={handleModeChange}
                    dataTypes={dataTypes}
                    errors={activeModeErrors}
                />
            </Drawer>
        </>
    );
};

export default ProfileDrawer;
